/**
 * Accordeonkoppen van de weergave **per gebouw** van de helpdesktickets.
 *
 * Niveau 1 = gebouw, niveau 2 = unit. De tellers komen uit de
 * backend-aggregatie en niet uit het aantal geladen rijen, zodat ze correct
 * blijven wanneer het ticketplafond de lijst afkapt.
 *
 * @module Helpdesk/Components/HelpdeskGroupHeaders
 */

import { useNavigate } from "react-router-dom";
import { Buildings, Cube, Warning } from "@phosphor-icons/react";
import { Badge } from "components/ui/badge";
import type {
  HelpdeskBuildingGroup,
  HelpdeskUnitGroup,
} from "../shared/hooks/useHelpdeskTicketGroups";

interface CountBadgesProps {
  openCount: number;
  completedCount: number;
  ticketCount: number;
}

/** Totaal / open / in afronding — zelfde volgorde als de statistieken bovenaan. */
const CountBadges = ({
  openCount,
  completedCount,
  ticketCount,
}: CountBadgesProps) => (
  <div className="flex shrink-0 items-center gap-1.5">
    <Badge variant="secondary">{ticketCount} tickets</Badge>
    {openCount > 0 && <Badge>{openCount} open</Badge>}
    {completedCount > 0 && (
      <Badge variant="outline">{completedCount} in afronding</Badge>
    )}
  </div>
);

interface BuildingGroupHeaderProps {
  group: HelpdeskBuildingGroup | undefined;
  fallbackCount?: number;
}

export const HelpdeskBuildingGroupHeader = ({
  group,
  fallbackCount = 0,
}: BuildingGroupHeaderProps) => {
  // De kop zit in de accordeon-trigger (een <button>); een <Link> daarin zou
  // ongeldige HTML-nesting geven, dus navigeren we programmatisch.
  const navigate = useNavigate();

  if (!group) {
    return (
      <span className="text-neutral-primary text-sm font-semibold">
        Onbekend gebouw ({fallbackCount})
      </span>
    );
  }

  return (
    <div className="flex min-w-0 flex-1 items-center gap-3">
      <Buildings className="text-moss-primary h-4 w-4 shrink-0" weight="duotone" />
      <span className="text-neutral-primary min-w-0 truncate text-sm font-semibold">
        {group.buildingId ?? "Zonder gebouw"}
      </span>
      {group.buildingId && (
        <span
          className="shrink-0 cursor-pointer"
          title="Open het gebouw"
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/buildings/${group.buildingId}`);
          }}
        >
          <Badge variant="outline">Open gebouw</Badge>
        </span>
      )}
      <div className="flex-1" />
      <CountBadges
        openCount={group.openCount}
        completedCount={group.completedCount}
        ticketCount={group.ticketCount}
      />
    </div>
  );
};

interface UnitGroupHeaderProps {
  unit: HelpdeskUnitGroup | undefined;
  fallbackCount?: number;
}

export const HelpdeskUnitGroupHeader = ({
  unit,
  fallbackCount = 0,
}: UnitGroupHeaderProps) => {
  if (!unit) {
    return (
      <span className="text-neutral-primary text-sm">
        Onbekende unit ({fallbackCount})
      </span>
    );
  }

  return (
    <div className="flex min-w-0 flex-1 items-center gap-2">
      <Cube className="text-neutral-secondary-mid h-4 w-4 shrink-0" weight="duotone" />
      <span className="text-neutral-primary min-w-0 truncate text-sm">
        {unit.unitId ?? "Zonder unit (gebouwbreed)"}
      </span>
      {/* Het plafond geldt per unit, dus zeg hier hoeveel er van deze unit
          geladen zijn — anders lijkt de groep gewoon kleiner dan ze is. */}
      {unit.truncated && (
        <span
          className="flex shrink-0 items-center gap-1 text-xs text-amber-700"
          title="Ticketlimiet bereikt — verfijn je filters om alles te zien"
        >
          <Warning className="h-3 w-3" weight="duotone" />
          {unit.tickets.length} van {unit.ticketCount} geladen
        </span>
      )}
      <div className="flex-1" />
      <CountBadges
        openCount={unit.openCount}
        completedCount={unit.completedCount}
        ticketCount={unit.ticketCount}
      />
    </div>
  );
};
